'use client'

import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'
import { useRouter } from 'next/navigation'

export function SearchButton() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const router = useRouter()

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function close() {
    setOpen(false)
    setQuery('')
    // Return focus to the trigger so keyboard users don't lose their place.
    requestAnimationFrame(() => triggerRef.current?.focus())
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    close()
    router.push(`/search?q=${encodeURIComponent(q)}`)
  }

  // Constrain Tab focus to input ↔ close button while overlay is open.
  function handleDialogKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return
    const els = [inputRef.current, closeRef.current].filter(Boolean) as HTMLElement[]
    if (els.length < 2) return
    if (e.shiftKey) {
      if (document.activeElement === els[0]) {
        e.preventDefault()
        els[els.length - 1].focus()
      }
    } else {
      if (document.activeElement === els[els.length - 1]) {
        e.preventDefault()
        els[0].focus()
      }
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen(true)}
        aria-label="Search"
        aria-expanded={open}
        aria-haspopup="dialog"
        className="relative text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
      >
        <Search size={20} />
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]"
            onClick={close}
            aria-hidden
          />
          {/* Dialog handles Escape to close — the intended a11y pattern; the
              rule flags the keyboard handler on role="dialog" as a false positive. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            onKeyDown={handleDialogKeyDown}
            className="fixed inset-x-0 top-0 z-50 bg-[var(--color-bg-primary)] border-b border-[var(--color-border)] shadow-md"
          >
            <div className="mx-auto max-w-[var(--max-width-page)] px-4 h-16 flex items-center gap-3">
              <Search size={20} className="shrink-0 text-[var(--color-text-muted)]" aria-hidden />
              <form onSubmit={handleSubmit} className="flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search products…"
                  aria-label="Search products"
                  maxLength={200}
                  className="w-full bg-transparent text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] text-base outline-none"
                />
              </form>
              <button
                ref={closeRef}
                type="button"
                onClick={close}
                className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                aria-label="Close search"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
